import { File } from '@epilot/pricing-client'

import { FileItem } from '../api/ApiTypes'
import { getCustomerApiClient } from '../api/CustomerApiClient'

import AuthUtils from './AuthUtils'

export const FILE_SIGNATURE_EXPIRATION = 1000 * 60 * 15

export const getFilePublicUrl = (file?: File): string | undefined =>
  file?.public_url || file?.versions?.[0]?.public_url

/**
 * A file marked `custom_download_url_auth === 'token'` is downloaded by sending
 * the portal user's bearer token to its download URL (exposed as `public_url`),
 * instead of following a presigned URL.
 *
 * Other files keep the presigned/HMAC behavior and must NOT receive the token.
 */
const TOKEN_AUTH_ALLOWED_HOSTS = ['epilot.io']

/**
 * The bearer token is only ever sent to a trusted epilot origin — a download
 * URL pointing anywhere else must never receive the user's token.
 */
const isTrustedTokenAuthUrl = (url: string | undefined): boolean => {
  if (!url) return false

  try {
    const { hostname } = new URL(url)

    return TOKEN_AUTH_ALLOWED_HOSTS.some(
      (host) => hostname === host || hostname.endsWith(`.${host}`)
    )
  } catch {
    return false
  }
}

export const isTokenAuthFile = (file: FileItem): boolean =>
  file.custom_download_url_auth === 'token' &&
  isTrustedTokenAuthUrl(file.public_url)

/**
 * Fetches a token-authenticated file by sending the portal user's bearer token
 * against its download URL (`public_url`). Returns the raw `Response` so callers
 * can read the blob and/or content-length headers.
 */
export const fetchTokenAuthFile = async (
  file: FileItem,
  init?: RequestInit
): Promise<Response> => {
  if (!isTrustedTokenAuthUrl(file.public_url)) {
    throw new Error('Refusing to send bearer token to untrusted download URL')
  }

  const token = await AuthUtils.getAccessToken()

  if (!token) {
    throw new Error('Missing portal user token for authenticated file download')
  }

  return fetch(file.public_url, {
    ...init,
    headers: {
      ...init?.headers,
      Authorization: `Bearer ${token}`
    }
  })
}

export const getDownloadUrl = async (file: FileItem): Promise<string> => {
  if (file.access_control === 'public-read') {
    return file.public_url
  }

  // Get the file from the API which includes the presigned public url
  const { data } = await getCustomerApiClient().getFileById({
    id: file._id
  })

  return data?.file?.public_url
}

type FileAccessParams = {
  file: FileItem
  isAdminSession?: boolean
}

export const getIsFileExtensionAllowed = (
  allowedExtensions: Record<string, string[]>
) => {
  const allowed = new Set(Object.values(allowedExtensions).flat())

  return (filename: string) => {
    const extension = filename.split('.').pop()

    return allowed.has(extension)
  }
}

const trackDownloaded = (file: FileItem, isAdminSession?: boolean) => {
  // Track the user has downloaded the file but only if it's not an admin
  // logged in as a customer
  if (!isAdminSession) {
    getCustomerApiClient().trackFileDownloaded({ id: file._id })
  }
}

export const openFile = async ({ file, isAdminSession }: FileAccessParams) => {
  // Token-mode files require the portal user's bearer token, which a plain
  // anchor navigation cannot attach. Fetch the blob with the auth header and
  // save it directly instead.
  if (isTokenAuthFile(file)) {
    const response = await fetchTokenAuthFile(file)

    if (!response.ok) {
      throw new Error(`Failed to download ${file.filename}: ${response.status}`)
    }

    const blob = await response.blob()
    const { saveAs } = await import('file-saver')

    saveAs(blob, file.filename)

    trackDownloaded(file, isAdminSession)

    return
  }

  const downloadUrl = await getDownloadUrl(file)

  const link = document.createElement('a')

  link.href = downloadUrl
  link.target = '_blank' // just in case it wouldn't be downloaded, but it should
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  trackDownloaded(file, isAdminSession)
}

export const getBinaryContent = async ({
  file,
  isAdminSession
}: FileAccessParams) => {
  const response = isTokenAuthFile(file)
    ? await fetchTokenAuthFile(file)
    : await fetch(await getDownloadUrl(file))

  if (!response.ok) {
    throw new Error(`Failed to download ${file.filename}: ${response.status}`)
  }

  trackDownloaded(file, isAdminSession)

  return response.blob()
}
